import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal, History, ArrowRight } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@vaiu/ui/pagination';
import { Transaction } from '@/services/platformApi';
import { sortTransactionsChronologically } from '@/utils/transactionUtils';
import TransactionItem from './TransactionItem';
import TransactionDetails from './TransactionDetails';

interface TransactionListProps {
  transactions: Transaction[];
  onTransactionSelect: (transaction: Transaction) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  loading: boolean;
  selectedTransaction?: Transaction | null;
  onCloseDetails?: () => void;
  // New props for Recent Activity mode
  showPagination?: boolean;
  emptyMessage?: string;
  onSeeAll?: () => void;
  // New props for separate sections
  pendingTransactions?: Transaction[];
  completedTransactions?: Transaction[];
  balanceOperations?: Transaction[]; // balance_up and balance_down transactions
  showSeparateSections?: boolean;
  sortMode?: 'chronological' | 'grouped'; // New prop for sorting mode
}

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  onTransactionSelect,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  loading,
  selectedTransaction,
  onCloseDetails,
  showPagination = true,
  emptyMessage,
  onSeeAll,
  pendingTransactions,
  completedTransactions,
  balanceOperations = [],
  showSeparateSections = false,
  sortMode = 'grouped'
}) => {
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (transactions.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <MoreHorizontal className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">
            {emptyMessage ? "No recent activity" : "No transactions found"}
          </h3>
          <p className="text-muted-foreground">
            {emptyMessage || "Try adjusting your search criteria or filters"}
          </p>
        </div>
        {onSeeAll && (
          <div className="text-center">
            <Button variant="outline" onClick={onSeeAll} className="w-full">
              <History className="w-4 h-4 mr-2" />
              View All Transactions
            </Button>
          </div>
        )}
      </div>
    );
  }

  const renderTransactionSection = (sectionTransactions: Transaction[], title?: string) => (
    <div className="space-y-0">
      {title && (
        <div className="px-1 py-2 bg-slate-50/50 border-b border-slate-200/60">
          <h3 className="text-sm font-medium text-slate-700">{title}</h3>
        </div>
      )}
      <div className="divide-y">
        {sectionTransactions.map((transaction, index) => {
          const transactionId = transaction.id || transaction.key || transaction._id;
          const selectedId = selectedTransaction?.id || selectedTransaction?.key || selectedTransaction?._id;
          const isSelected = selectedTransaction && selectedId === transactionId;
          
          // Create unique key by combining transaction ID with index to handle duplicates
          const uniqueKey = `${transactionId}-${index}`;
          
          return (
            <div key={uniqueKey}>
              <TransactionItem
                transaction={transaction}
                onClick={() => onTransactionSelect(transaction)}
                isLast={index === sectionTransactions.length - 1 && !isSelected}
              />
              {/* Inline Transaction Details - appears right after the clicked item */}
              {isSelected && onCloseDetails && (
                <div className="bg-slate-50/50">
                  <TransactionDetails
                    transaction={selectedTransaction}
                    onClose={onCloseDetails}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-0">
      {/* Render separate sections or combined list */}
      {showSeparateSections && (pendingTransactions || completedTransactions || balanceOperations.length > 0) ? (
        <div className="space-y-0">
          {/* Balance Operations Section - Show first (topups and transfers) */}
          {balanceOperations.length > 0 && (
            renderTransactionSection(balanceOperations, "Balance Operations")
          )}
          
          {/* Active Tokens Section - Show second */}
          {pendingTransactions && pendingTransactions.length > 0 && (
            renderTransactionSection(pendingTransactions, "Active Tokens")
          )}
          
          {/* Completed/Expired Tokens Section - Show third */}
          {completedTransactions && completedTransactions.length > 0 && (
            renderTransactionSection(completedTransactions, "Completed & Expired Tokens")
          )}
        </div>
      ) : (
        /* Combined Transaction List (chronological mode) */
        renderTransactionSection(
          sortMode === 'chronological' 
            ? sortTransactionsChronologically(transactions) // transactions already includes all transactions (including balance ops) sorted chronologically
            : [...transactions, ...balanceOperations]
        )
      )}

      {/* See All Transactions Button (for Recent Activity mode) */}
      {onSeeAll && (
        <div className="pt-2 mt-1 border-t border-gray-200/60 px-1">
          <Button variant="outline" onClick={onSeeAll} className="w-full">
            <History className="w-4 h-4 mr-2" />
            See All Transactions
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Pagination (for All Transactions mode) */}
      {showPagination && totalPages > 1 && onPageChange && (
        <div className="border-t bg-background p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    size="sm"
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {generatePageNumbers().map((page, index) => (
                  <PaginationItem key={index}>
                    {page === '...' ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        onClick={() => onPageChange(page as number)}
                        isActive={currentPage === page}
                        size="sm"
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    size="sm"
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionList; 